import { useAtomValue } from "jotai";
import { useState } from "react";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { animated, useTransition } from "react-spring";
import { getCardLayoutInfo } from "../../../modules/getCardLayoutInfo";
import { pushGTMAnalyticsEvent } from "../../../modules/pushGTMAnalyticsEvent";
import { all_sections_array } from "../../dataManagers/GlobalDataManagers";
import { Boop } from "../Animations/Boop";
import { ImgWithFallback } from "../ImgWithFallback/ImgWithFallback";
import { ProductDetailsModal } from "../Modal/ProductDetailsModal";
import "./styles.scss";

export const CardElementAccessories = ({ itemsStateObj, accessoriesArray, cardPerRow = 3, isInAxysPackage = false }) => {
  const [show, setShow] = useState(false);
  const sectionsArray = useAtomValue(all_sections_array);
  const [modalDetails, setModalDetails] = useState({});
  const handleCloseModal = () => setShow(false);
  const handleShowModal = () => setShow(true);
  const cardLayoutInfo = getCardLayoutInfo(cardPerRow);

  const dataToDisplayTransitionArray = useTransition(accessoriesArray, {
    trail: 100,
    from: { opacity: 0, scale: 0 },
    enter: { opacity: 1, scale: 1 },
  });

  const removeItemFromActiveIdArray = (copyActiveIds, itemToRemove) => {
    const itemToRemoveIndex = copyActiveIds.accessories.array.map((object) => object._id)?.indexOf(itemToRemove);
    copyActiveIds.accessories.array.splice(itemToRemoveIndex, 1);

    pushGTMAnalyticsEvent({
      event: "Accessories Click - MojoDesk",
      accessoryClickAction: "removed",
      accessoryType: itemToRemove.includes("monitor") ? "Monitor" : "Accessory",
      id: itemToRemove,
      page: "accessories",
    });

    return copyActiveIds;
  };

  const addOrRemoveAccessory = (copyActiveIds, cardId) => {
    // Check if this is an item part of an Axys package
    const selectedAxysLevel = itemsStateObj.state.activeIds.accessories.array.find((item) => item._id?.includes("axys_level"));

    if (selectedAxysLevel) {
      const axysLevelObj = itemsStateObj.state.array.find((item) => item._id === selectedAxysLevel._id);
      if (axysLevelObj?.includedAccessories?.includes(cardId)) {
        // This item is part of the selected Axys package, don't allow removal
        console.log(`Cannot remove ${cardId} as it's part of Axys package ${selectedAxysLevel._id}`);
        return copyActiveIds;
      }
    }

    // Find the full item object from the itemsStateObj array
    const itemObject = itemsStateObj.state.array.find((item) => item._id === cardId);

    if (!itemObject) {
      console.error(`Item with ID ${cardId} not found in items array`);
      return copyActiveIds;
    }

    // SAFEGUARD: Do not add if excluded (should be handled by global data manager, but double check)
    if (itemObject.excluded) {
      console.warn(`Attempted to add excluded item ${cardId} to activeIds. This should not happen.`);
      return copyActiveIds;
    }

    // Existing logic for non-Axys-package items
    // check if item is already in the activeIds
    if (itemsStateObj.state.activeIds.accessories.array.find((item) => item._id === cardId)) {
      return removeItemFromActiveIdArray(copyActiveIds, cardId);
    }

    // Add only the ID to the array, not the complete item object
    copyActiveIds.accessories.array.push({
      _id: cardId,
    });

    pushGTMAnalyticsEvent({
      event: "Accessories Click - MojoDesk",
      accessoryClickAction: "added",
      accessoryType: "Accessory",
      id: cardId,
      page: "accessories",
    });

    return copyActiveIds;
  };

  const addOrRemoveMonitor = (copyActiveIds, cardId) => {
    const activeObj = itemsStateObj.state.activeObjs.desk_model._id;
    const monitorSection = sectionsArray.find((section) => section._id === "monitors");
    // check if a monitor is already active
    const monitorFound = itemsStateObj.state.activeObjs.accessories.array.find((item) => item.section === "monitors");

    // check if card clicked was the active card
    if (monitorFound?._id === cardId && !monitorSection?.required?.includes(activeObj)) {
      return removeItemFromActiveIdArray(copyActiveIds, monitorFound._id);
    }

    // check if a monitor is already active
    if (monitorFound) {
      copyActiveIds = removeItemFromActiveIdArray(copyActiveIds, monitorFound._id);
    }

    // Find the full item object from the itemsStateObj array
    const itemObject = itemsStateObj.state.array.find((item) => item._id === cardId);
    // SAFEGUARD: Do not add if excluded (should be handled by global data manager, but double check)
    if (itemObject && itemObject.excluded) {
      console.warn(`Attempted to add excluded monitor ${cardId} to activeIds. This should not happen.`);
      return copyActiveIds;
    }

    // Add only the ID object, not the complete monitor object
    copyActiveIds.accessories.array.push({
      _id: cardId,
    });

    pushGTMAnalyticsEvent({
      event: "Accessories Click - MojoDesk",
      accessoryClickAction: "added",
      accessoryType: "Monitor",
      id: cardId,
      page: "accessories",
    });

    return copyActiveIds;
  };

  const handleCardClick = (e, clickedCard) => {
    if (clickedCard?.metaData?.isSoldOut) return;

    // Prevent card click for any type of included item
    const isAnyIncluded = clickedCard?.metaData?.isIncluded || clickedCard?.metaData?.isAxysIncluded;

    // prevents card from firing when details button clicked or for any included item
    if (e.target.id === "modalButton" || isAnyIncluded) return;

    let copyActiveIds = { ...itemsStateObj.state.activeIds };

    // Updated condition to handle any non-monitor accessories with addOrRemoveAccessory
    copyActiveIds =
      clickedCard.section === "monitors" ? addOrRemoveMonitor(copyActiveIds, clickedCard._id) : addOrRemoveAccessory(copyActiveIds, clickedCard._id);

    itemsStateObj.setState(copyActiveIds);
  };

  const handleSeeProductDetailsClick = (e, _modalDetails) => {
    setModalDetails({ ..._modalDetails });
    handleShowModal();
    // if(EmbedController.isEmbedded) EmbedController.sendGTMAnalyticsEvent({ 'event': 'Product Details Click - MGP' });
  };

  return (
    <Row className="CardElementAccessories">
      {dataToDisplayTransitionArray((style, card, key, index) => {
        if (card.excluded) return null;

        // Determine if the card represents an included accessory
        const isRegularIncluded = card?.metaData?.isIncluded;
        const isAxysIncluded = card?.metaData?.isAxysIncluded;
        const isAnyIncluded = isRegularIncluded || isAxysIncluded;

        // Only show as active if it's not included in any way
        const isActive = !isAnyIncluded && itemsStateObj.state.activeIds.accessories.array.find((item) => item._id === card._id) ? true : false;

        const isSoldOut = card?.metaData?.isSoldOut;

        // Check if this is an accessory from an Axys package
        const axysLevelItem = itemsStateObj.state.activeIds.accessories.array.find((item) => item._id?.includes("axys_level"));
        const axysLevelObj = axysLevelItem ? itemsStateObj.state.array.find((item) => item._id === axysLevelItem._id) : null;
        const isPartOfAxysPackage = axysLevelObj?.includedAccessories?.includes(card._id);

        const cardClassName = `mb-3 itemsCard 
          ${isActive ? "active" : ""} 
          ${isSoldOut ? "soldOut" : ""} 
          ${isPartOfAxysPackage ? "axys-included" : ""}
          ${isRegularIncluded ? "included-item" : ""}
          ${isInAxysPackage ? "included-axys-package-card" : ""}
        `;

        return (
          <Col xs={cardLayoutInfo.xs} md={cardLayoutInfo.md} key={card._id} className="px-2">
            <animated.div style={style}>
              <Card className={cardClassName} style={{ width: cardLayoutInfo.width }} onClick={(e) => handleCardClick(e, card)}>
                <div className="d-flex">
                  {isActive ? <img className="checkMark" alt="check mark" src="/images/checkMark.svg" /> : null}
                  {/* {!isAnyIncluded && card?.price && <div className="price ms-auto px-2">{`$${card.price}`}</div>} */}
                </div>
                <div className="mt-3 mb-1 d-flex mx-auto">
                  <ImgWithFallback src={card.thumbnail[0]} fallback={card.thumbnail[1] || card.thumbnail[0]} alt={card.displayName} className="card-img-top" />
                </div>
                <Card.Body className="py-2 d-flex flex-column justify-content-center align-items-center">
                  {card.displayName && <h6 className="title">{card.displayName} </h6>}
                  {card?.multiLineDisplayName &&
                    card.multiLineDisplayName.map((line) => (
                      <p className="multiLineDisplayName mb-0" key={line}>
                        {line}
                      </p>
                    ))}
                  {card?.description && <p className="description mb-1">{card.description} </p>}
                  {card?.modalDetails && (
                    <div className="d-flex justify-content-center my-1">
                      <Boop boopType="scale" scale={1.03} timing={200}>
                        <button id="modalButton" onClick={(e) => handleSeeProductDetailsClick(e, card.modalDetails)}>
                          more info
                        </button>
                      </Boop>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </animated.div>
          </Col>
        );
      })}
      <ProductDetailsModal show={show} modalDetails={modalDetails} onHide={handleCloseModal} className="ProductDetailsModal" />
    </Row>
  );
};
